import React, { useState, useEffect } from 'react';
import { X, Save, Hash, Wrench, Box, Shield, Settings, Database, Loader2, Briefcase, Activity } from 'lucide-react';
import axios from 'axios'; 
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next'; // 🎯 استدعاء الترجمة

const AppSettingsModal = ({ isOpen, onClose, orgId }) => {
  const { t } = useTranslation(); // 🎯 تفعيل الـ Hook
  const [activeTab, setActiveTab] = useState('WORKORDER');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [config, setConfig] = useState({
    WORKORDER: { PREFIX: '', SEED: '' },
    ASSET: { PREFIX: '', SEED: '' },
    INVENTORY: { PREFIX: '', SEED: '', COST_METHOD: 'MOVING_AVG', ALLOW_NEG: 'false'},
    PURCHASE: { PREFIX: '', SEED: '' },
    PR: {PREFIX: '', SEED: '' },
    JP: {PREFIX: '', SEED: '' },
    PM: {PREFIX: '', SEED: '' },
    MR: {PREFIX: '', SEED: ''},
    SR: {PREFIX: '', SEED: ''}
  });

  useEffect(() => {
    if (isOpen && orgId) {
      fetchSettings();
    }
  }, [isOpen, orgId]);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/settings/${orgId}`);
      const newConfig = { ...config };
      response.data.forEach(item => {
        if (newConfig[item.app_name]) {
          newConfig[item.app_name][item.setting_key] = item.setting_value;
        }
      });
      setConfig(newConfig);
    } catch (error) {
      console.error("Error fetching settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = [];
      Object.keys(config).forEach(app => {
        Object.keys(config[app]).forEach(key => {
          payload.push({
            org_id: orgId,
            app_name: app,
            setting_key: key,
            setting_value: String(config[app][key])
          });
        });
      });
      const loadId = toast.loading(t('appSettings.savingSettings')); // 🎯
      await axios.post('/api/settings/update', payload);
      toast.success(t('appSettings.saveSuccess'), { id: loadId }); // 🎯
      onClose();
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error(t('appSettings.saveFailed'), { id: loadId }); // 🎯
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const TabItem = ({ id, label, icon: Icon }) => (
    <button
      onClick={() => setActiveTab(id)}
      className={`w-full flex items-center gap-3 px-4 py-4 text-[11px] font-black uppercase tracking-widest transition-all ${
        activeTab === id ? 'bg-[#1a3353] text-white shadow-lg' : 'text-gray-500 hover:bg-gray-100'
      }`}
    >
      <Icon size={16} /> {label}
    </button>
  );

  // مسميات التابات للترجمة
  const tabLabels = {
    WORKORDER: t('appSettings.tabs.WORKORDER'),
    ASSET: t('appSettings.tabs.ASSET'),
    INVENTORY: t('appSettings.tabs.INVENTORY'),
    PURCHASE: t('appSettings.tabs.PURCHASE'),
    PR: t('appSettings.tabs.PR'),
    JP: t('appSettings.tabs.JP'),
    PM: t('appSettings.tabs.PM'),
    MR: t('appSettings.tabs.MR'),
    SR: t('appSettings.tabs.SR')
  };

  return (
    <div className="fixed inset-0 z-[7000] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className="bg-white w-full max-w-4xl h-[600px] flex flex-col shadow-2xl rounded-sm overflow-hidden border border-white/20">
        
        {/* Header */}
        <div className="bg-[#1a3353] p-4 flex justify-between items-center text-white">
          <div className="flex items-center gap-3">
            <Settings className={loading || saving ? "animate-spin" : "animate-spin-slow"} />
            <div className="flex flex-col text-left">
              <span className="text-xs font-black uppercase tracking-[0.2em]">{t('appSettings.title')}</span>
              <span className="text-[9px] text-blue-300 font-bold uppercase">{t('appSettings.org')}: {orgId}</span>
            </div>
          </div>
          <button onClick={onClose} className="hover:bg-red-500 p-1 rounded-full transition-colors"><X /></button>
        </div>

        <div className="flex flex-1 overflow-hidden relative">
          {loading && (
            <div className="absolute inset-0 bg-white/50 z-10 flex items-center justify-center backdrop-blur-[1px]">
               <Loader2 className="animate-spin text-[#1a3353]" size={40} />
            </div>
          )}

          {/* Sidebar Tabs */}
          <div className="w-64 bg-gray-50 border-r p-2 space-y-1">
            <TabItem id="WORKORDER" label={tabLabels.WORKORDER} icon={Wrench} />
            <TabItem id="ASSET" label={tabLabels.ASSET} icon={Database} />
            <TabItem id="INVENTORY" label={tabLabels.INVENTORY} icon={Box} />
            <TabItem id="PURCHASE" label={tabLabels.PURCHASE} icon={Shield} />
            <TabItem id="PR" label={tabLabels.PR} icon={Shield} />
            <TabItem id="JP" label={tabLabels.JP} icon={Briefcase} />
            <TabItem id="PM" label={tabLabels.PM} icon={Activity} />
            <TabItem id="MR" label={tabLabels.MR} icon={Box} />
            <TabItem id="SR" label={tabLabels.SR} icon={Box} />
          </div>

          {/* Settings Content */}
          <div className="flex-1 p-10 overflow-y-auto bg-white text-left custom-scrollbar">
            <div className="flex justify-between items-center mb-8 border-b pb-4">
               <h2 className="text-xl font-black text-gray-800 uppercase tracking-tighter">
                {t('appSettings.systemRules', { tab: tabLabels[activeTab] })}
              </h2>
            </div>

            <div className="grid grid-cols-2 gap-10">
              {/* Prefix Setting */}
              {config[activeTab]?.PREFIX !== undefined && (
                <div className="space-y-3">
                  <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest flex items-center gap-2">
                    <Hash size={12} className="text-blue-600"/> {t('appSettings.prefix')}
                  </label>
                  <input 
                    type="text" 
                    value={config[activeTab].PREFIX}
                    onChange={(e) => setConfig({...config, [activeTab]: {...config[activeTab], PREFIX: e.target.value}})}
                    className="w-full border-b-2 border-gray-100 p-3 text-sm font-black focus:border-[#1a3353] outline-none transition-all bg-gray-50 focus:bg-white"
                  />
                  <p className="text-[9px] text-gray-400 font-bold italic uppercase tracking-tighter">{t('appSettings.preview')}: {config[activeTab].PREFIX}-2026-0001</p>
                </div>
              )}

              {/* Seed Setting */}
              {config[activeTab]?.SEED !== undefined && (
                <div className="space-y-3">
                  <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest flex items-center gap-2">
                    <Database size={12} className="text-blue-600"/> {t('appSettings.seed')}
                  </label>
                  <input 
                    type="number" 
                    value={config[activeTab].SEED}
                    onChange={(e) => setConfig({...config, [activeTab]: {...config[activeTab], SEED: e.target.value}})}
                    className="w-full border-b-2 border-gray-100 p-3 text-sm font-black focus:border-[#1a3353] outline-none transition-all bg-gray-50 focus:bg-white"
                  />
                </div>
              )}

              {/* Inventory Specifics */}
              {activeTab === 'INVENTORY' && (
                <>
                  <div className="space-y-3 col-span-2">
                    <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest">{t('appSettings.valuationStrategy')}</label>
                    <select 
                      value={config.INVENTORY.COST_METHOD}
                      onChange={(e) => setConfig({...config, INVENTORY: {...config.INVENTORY, COST_METHOD: e.target.value}})}
                      className="w-full border-b-2 border-gray-100 p-3 text-sm font-black outline-none bg-gray-50 focus:border-[#1a3353]"
                    >
                      <option value="MOVING_AVG">{t('appSettings.movingAvg')}</option>
                      <option value="FIFO">{t('appSettings.fifo')}</option>
                      <option value="LIFO">{t('appSettings.lifo')}</option>
                    </select>
                  </div>
                  <div className="col-span-2 flex items-center justify-between p-5 bg-blue-50/50 border border-blue-100 rounded-sm">
                    <div className="flex flex-col">
                        <span className="text-[10px] font-black text-[#1a3353] uppercase">{t('appSettings.allowNegative')}</span>
                        <span className="text-[8px] font-bold text-blue-400 uppercase">{t('appSettings.negativeWarning')}</span>
                    </div>
                    <input 
                      type="checkbox" 
                      checked={config.INVENTORY.ALLOW_NEG === 'true'}
                      onChange={(e) => setConfig({...config, INVENTORY: {...config.INVENTORY, ALLOW_NEG: String(e.target.checked)}})}
                      className="w-6 h-6 accent-[#1a3353] cursor-pointer" 
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Action Footer */}
        <div className="p-4 border-t bg-gray-50 flex justify-between items-center px-8">
          <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest">
            {saving ? t('appSettings.encrypting') : t('appSettings.auditLogged')}
          </span>
          <div className="flex gap-4">
            <button 
              onClick={onClose} 
              disabled={saving}
              className="px-6 py-2 text-[10px] font-black uppercase text-gray-400 hover:text-gray-800 transition-all disabled:opacity-50"
            >
              {t('appSettings.btnCancel')}
            </button>
            <button 
              onClick={handleSave} 
              disabled={saving || loading}
              className="px-10 py-3 bg-[#1a3353] hover:bg-[#2c4a70] text-white text-[10px] font-black uppercase shadow-xl flex items-center gap-3 transition-all disabled:bg-gray-400"
            >
              {saving ? <Loader2 className="animate-spin" size={14}/> : <Save size={14} />} 
              {t('appSettings.btnSave')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppSettingsModal;